import Main from "~/js/Main";

class DropdownMenu {
	/**
	 * @type {null|HTMLElement}
	 */
	container = null;
	/**
	 * @type {null|HTMLElement}
	 */
	navigation = null;
	/**
	 * @type {null|HTMLLIElement}
	 */
	currentMenuItem = null;
	/**
	 * @type {null|Dropdown}
	 */
	currentDropdown = null;
	/**
	 * @type {Dropdown[]}
	 */
	dropdowns = [];
	currentLiClassName = "has-active-dropdown";
	openTimeout = 0;
	closeTimeout = 0;
	openDelay = 150;
	closeDelay = 400;


	/**
	 *
	 * @param {HTMLElement} navigation
	 * @param {HTMLElement} container
	 */
	constructor(navigation, container) {
		this.navigation = navigation;
		this.container = container;
		/**
		 *
		 * @type {NodeListOf<HTMLLIElement>}
		 */
		this.menuItems = navigation.querySelectorAll("li");
		/**
		 * @type {NodeListOf<HTMLLIElement>}
		 */
		this.parentMenuItems = navigation.querySelectorAll("li[class*=children]");
		/**
		 * @type {NodeListOf<HTMLUListElement>}
		 */
		navigation.querySelectorAll("ul.dropdown")
			.forEach(ul => {
				this.dropdowns.push(new Dropdown(ul));
			});

		if (this.dropdowns.length > 0) {
			this.addHandlers();
		}
	}

	addHandlers() {
		const debounce = Main.Instance.debounce;

		this.menuItems.forEach(item => {
			item.addEventListener("mouseenter", debounce(this.onMenuItemMouseOver.bind(this)));
			item.addEventListener("mouseleave", debounce(this.onMenuItemMouseOut.bind(this)));
		});

		this.parentMenuItems.forEach(item => {
			item.addEventListener("focusin", debounce(this.onMenuItemMouseOver.bind(this)));
			item.addEventListener("focusout", debounce(this.onMenuItemMouseOut.bind(this)));
			item.addEventListener("touchstart", debounce(this.onMenuItemTouch.bind(this)));
			item.querySelector("a")?.addEventListener("mouseenter", debounce(this.onMenuItemLinkMouseOver.bind(this)));
		});

		this.container.addEventListener("mouseleave", debounce(this.onContainerMouseOut.bind(this)));
	}

	/**
	 * @param {HTMLUListElement|null} ul
	 * @return {number}
	 */
	getLevel(ul) {
		if (!ul) {
			return -1;
		}

		for (let key of ul.classList) {
			if (key.match(/lvl-\d/)) {
				return parseInt(key.replace("lvl-", ""));
			}
		}

		return -1;
	}

	/**
	 *
	 * @param {HTMLLIElement} li
	 * @return {boolean}
	 */
	hasDropdown(li) {
		return this.getDropdown(li) !== null;
	}

	/**
	 * @param {HTMLLIElement} li
	 * @return {Dropdown|null}
	 */
	getDropdown(li) {
		let out = null;
		this.dropdowns.forEach(item => {
			if (item.element.isEqualNode(li.querySelector(".dropdown"))) {
				out = item;
			}
		});
		return out;
	}

	/**
	 * @param {HTMLLIElement} li
	 * @return {Dropdown[]}
	 */
	getParentDropdowns(li) {
		let out = [];
		const ul = li.parentElement;
		if (ul instanceof HTMLUListElement) {
			this.dropdowns.forEach(item => {
				if (item.element.isEqualNode(ul)) {
					out.push(item);
					out = out.concat(this.getParentDropdowns(ul.parentElement));
				}
			});
		}

		return out;
	}

	collapseDropdowns() {
		/**
		 * @type {Dropdown[]}
		 */
		const collapsible = [];
		this.dropdowns.forEach(item => {
			item.isCollapsible = item.isActive;

			if (item.isCollapsible) {
				if (this.currentDropdown && this.currentDropdown === item) {
					item.isCollapsible = false;
				}

				if (this.currentMenuItem && this.getParentDropdowns(this.currentMenuItem).indexOf(item) > -1) {
					item.isCollapsible = false;
				}

				if (this.currentMenuItem && this.getDropdown(this.currentMenuItem) === item) {
					item.isCollapsible = false;
				}
			}

			if (item.isCollapsible) {
				collapsible.push(item);
			}
		});

		collapsible.forEach(item => {
			if (item.isCollapsible) {
				item.hide();
			}
		});
	}

	/**
	 * @param {Event} e
	 */
	onMenuItemExpanded(e) {
		/**
		 * @type {HTMLLIElement}
		 */
		const item = e.target;
		item.querySelector("a").setAttribute("aria-expanded", "true");
	}

	/**
	 * @param {Event} e
	 */
	onMenuItemCollapsed(e) {
		/**
		 * @type {HTMLLIElement}
		 */
		const item = e.target;
		item.querySelector("a").setAttribute("aria-expanded", "false");
	}

	/**
	 * @param {Event} e
	 */
	onMenuItemMouseOver(e) {
		/**
		 * @type {HTMLLIElement}
		 */
		const item = e.target;

		if (item instanceof HTMLLIElement) {
			this.currentMenuItem = item;
		}
		else {
			this.currentMenuItem = item.parentElement;
		}

		this.currentDropdown = this.getDropdown(this.currentMenuItem);

		if (this.currentDropdown) {
			this.currentDropdown.show();
		}
	}

	/**
	 * @param {Event} e
	 */
	onMenuItemMouseOut(e) {
		this.currentMenuItem = null;

		window.setTimeout(this.collapseDropdowns.bind(this), Dropdown.showDelay);
	}

	/**
	 * @param {Event} e
	 */
	onContainerMouseOut(e) {
		this.currentMenuItem = null;
		this.currentDropdown = null;

		this.collapseDropdowns();
	}

	/**
	 * @param {TouchEvent} e
	 */
	onMenuItemTouch(e) {
		/**
		 * @type {HTMLLIElement}
		 */
		const item = e.target;
		const submenu = this.getDropdown(item);

		this.currentMenuItem = item;

		if (submenu) {
			if (!submenu.isActive) {
				e.preventDefault();
			}
			this.currentDropdown = submenu;
			this.currentDropdown.show();
			this.collapseDropdowns();
		}
	}

	/**
	 *
	 * @param {Event} e
	 */
	onMenuItemLinkMouseOver(e) {
		/**
		 * @type {HTMLLinkElement}
		 */
		const a = e.target;
		this.currentMenuItem = a.parentElement;
	}
}

class Dropdown {
	/**
	 * @type {HTMLUListElement}
	 */
	element = null;
	/**
	 * @type {HTMLLIElement}
	 */
	container = null;
	/**
	 * @type {boolean}
	 */
	isActive = false;
	/**
	 * @type {boolean}
	 */
	isTransitioning = false;
	/**
	 * @type {boolean}
	 */
	isCollapsible = true;
	/**
	 * @type {string}
	 */
	className = "dropdown";
	/**
	 * @type {string}
	 */
	activeClassName = "active";
	/**
	 * @type {string}
	 */
	activeContainerClassName = "has-active-dropdown";

	showTimeout = 0;
	hideTimeout = 0;

	static showDelay = 150;
	static hideDelay = 250;

	/**
	 * @param {HTMLUListElement} element
	 */
	constructor(element) {
		this.element = element;
		this.container = element.parentElement;
	}

	/**
	 * @return Dropdown|null
	 */
	getParent() {

	}

	/**
	 * @param {boolean} show
	 */
	toggle(show) {
		if (show) {
			this.show();
		}
		else {
			this.hide();
		}
	}

	show() {
		window.clearTimeout(this.hideTimeout);
		this.isTransitioning = true;
		this.isActive = true;

		this.showTimeout = window.setTimeout(() => {
			this.element.classList.add(this.activeClassName);
			this.container.classList.add(this.activeContainerClassName);
			this.isTransitioning = false;
		}, Dropdown.showDelay);
	}

	hide() {
		window.clearTimeout(this.showTimeout);
		this.isActive = false;
		this.isTransitioning = true;

		this.hideTimeout = window.setTimeout(() => {
			this.element.classList.remove(this.activeClassName);
			this.container.classList.remove(this.activeContainerClassName);
			this.isTransitioning = false;
		}, Dropdown.hideDelay);
	}
}

export default DropdownMenu;
